import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldAlert, Download, ClipboardPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, download } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS, PRIORITY } from "@/lib/constants";
import { PageHead, Panel, Empty, StatusBadge, PriorityBadge, SButton, sInput, sLabel } from "@/components/staff/Bits";
import Filters, { clean, useGkks, gkkName } from "@/components/staff/Filters";

const GROUPS = { new: "New", review: "Awaiting review", assisting: "Being assisted", followup: "Follow-up", referred: "Referred", completed: "Completed / closed", further_action: "Further action" };

function ExportButton() {
  const run = async () => {
    const reason = window.prompt("Identifiable export is restricted. State the legitimate purpose (recorded in the audit log):");
    if (!reason || reason.trim().length < 5) return;
    try { await download("/cases-export", { reason }, "mabuhay-care-cases-CONFIDENTIAL.csv"); toast.success("Export recorded in audit log"); } catch (e) { toast.error(errMsg(e)); }
  };
  return <SButton variant="ghost" data-testid="export-identifiable-button" onClick={run}><Download className="w-4 h-4" /> Identifiable export</SButton>;
}

export default function Cases() {
  const { can } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const gkks = useGkks();
  const [filters, setFilters] = useState(() => ({ status: params.get("status") || "", priority: params.get("priority") || "", overdue: params.get("overdue") === "1", q: "" }));
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get("/cases", { params: { ...clean(filters), page } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [filters, page]);
  const upd = (f) => { setPage(1); setFilters(f); };

  const extra = (<>
    <div><label className={sLabel} htmlFor="fs">Status</label>
      <select id="fs" data-testid="filter-status" className={sInput} value={filters.status} onChange={(e) => upd({ ...filters, status: e.target.value })}>
        <option value="">All</option><optgroup label="Groups">{Object.entries(GROUPS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</optgroup>
        <optgroup label="Exact status">{Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</optgroup>
      </select></div>
    <div><label className={sLabel} htmlFor="fp">Priority</label>
      <select id="fp" data-testid="filter-priority" className={sInput} value={filters.priority} onChange={(e) => upd({ ...filters, priority: e.target.value })}>
        <option value="">All</option><option value="unset">Not yet prioritized</option>{Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select></div>
    <div className="col-span-2 md:col-span-1"><label className={sLabel} htmlFor="fq">Reference</label>
      <input id="fq" data-testid="filter-reference" className={sInput} placeholder="MBC-2026-" value={filters.q} onChange={(e) => upd({ ...filters, q: e.target.value })} /></div>
    <label className="flex items-end gap-2 pb-2 text-sm font-semibold"><input type="checkbox" data-testid="filter-overdue" className="w-5 h-5 accent-[#B85028]" checked={filters.overdue} onChange={(e) => upd({ ...filters, overdue: e.target.checked })} /> Overdue follow-up only</label>
  </>);

  return (
    <div>
      <PageHead title="Cases" sub="Each request is a person or family deserving attention. Descriptions are shown only inside the case record.">
        {can("cases:intake") && <Link to="/office/intake"><SButton data-testid="new-intake-button"><ClipboardPlus className="w-4 h-4" /> Office intake</SButton></Link>}
        {can("cases:export_identifiable") && <ExportButton />}
      </PageHead>
      <Filters value={filters} onChange={upd} extra={extra} />
      <Panel className="p-0 sm:p-0 overflow-hidden">
        {!data ? <Empty>Loading…</Empty> : data.items.length === 0 ? <Empty testid="cases-empty">No cases match these filters.</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full text-[15px]" data-testid="cases-table">
              <thead className="bg-[#FAF8F5] text-left text-xs uppercase tracking-wider text-[#605349]">
                <tr><th className="px-4 py-3">Reference</th><th className="px-4 py-3">Submitted</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Categories</th><th className="px-4 py-3">GKK</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Priority</th><th className="px-4 py-3">Assigned</th><th className="px-4 py-3">Next follow-up</th></tr>
              </thead>
              <tbody>
                {data.items.map((c) => (
                  <tr key={c.id} data-testid={`case-row-${c.reference}`} onClick={() => nav(`/office/cases/${c.id}`)} className="border-t border-[#EFE8DF] hover:bg-[#FDF7E7] cursor-pointer">
                    <td className="px-4 py-3 font-mono font-bold text-[#4A2E1A] whitespace-nowrap">{c.reference}{c.safeguarding && <ShieldAlert className="inline w-4 h-4 ml-1 text-[#B85028]" aria-label="Safeguarding" />}
                      {c.verification?.status === "suspected_false" && <span className="ml-1 text-xs font-bold text-[#8A2E14]">FLAGGED</span>}
                      {c.possible_duplicates?.length > 0 && <span className="ml-1 text-xs font-bold text-[#78350F]">repeat?</span>}
                      {(c.verification?.status || "unverified") === "unverified" && <span className="block text-[11px] font-semibold text-[#7A5A10] font-sans">not verified</span>}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                    <td className="px-4 py-3">{c.requester?.preferred_name || c.requester?.full_name}</td>
                    <td className="px-4 py-3">{c.categories.join(", ")}</td>
                    <td className="px-4 py-3">{gkkName(gkks, c.gkk)}</td>
                    <td className="px-4 py-3"><StatusBadge status={c.status} testid={`row-status-${c.reference}`} />{c.requester_replied && <span className="ml-1 text-xs font-bold text-[#3D6346]">reply</span>}</td>
                    <td className="px-4 py-3"><PriorityBadge priority={c.priority} testid={`row-priority-${c.reference}`} /></td>
                    <td className="px-4 py-3">{c.assigned_name || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{c.next_followup_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {data && data.total > 25 && (
        <div className="flex items-center gap-3 mt-4">
          <SButton variant="ghost" data-testid="prev-page" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</SButton>
          <span className="text-sm">Page {page} of {Math.ceil(data.total / 25)}</span>
          <SButton variant="ghost" data-testid="next-page" disabled={page * 25 >= data.total} onClick={() => setPage(page + 1)}>Next</SButton>
        </div>
      )}
    </div>
  );
}
